import { actions as notifActions } from 'redux-notifications';
import * as i18n from 'i18next';
import {
	IAppointmentState,
	IAppointmentAction,
	IAppointment,
	IOrdersRequest,
	IAppointmentWithCustomer,
	IAppointmentFilters,
	IAppointmentStatistics,
	IManualAppointment,
	BookingItemToUpdateRequest,
	IHoliday,
} from 'models/shop/appointments';
import { IStore } from 'redux/IStore';
import moment = require('moment');
import { Dispatch } from 'react-redux';
import { IOffice } from 'models/shop/office';
import { IPage } from 'models/common';
import { ICustomer } from 'models/shop/customer';
import { momentWithTimezone, normalizeDate } from '../helpers';
import { requestHelper } from 'redux/helpers';
import { Periodicity } from 'models/enums/periodicity';
import { unitOfTime } from 'moment';
import {
	IManualOrderRequest,
	IOrderItem,
	IOrderRequest,
	IShopOrder,
	IUpdateShopOrderRequest,
} from 'models/orders/order';
import { addBookingToCart, clearShoppingBasket, createOrder } from 'redux/modules/cart';
import { IAddBookingItemRequest } from 'models/bookappointment';
import { OrderStatus } from 'models';
import { getShopOrder } from '../shopordercard';
import * as c from 'models/constants';
const { notifSend } = notifActions;

/** Action Types */
export const REQUEST_START: string = 'appointments/REQUEST_START';
export const REQUEST_FAILURE: string = 'appointments/REQUEST_FAILURE';
export const REQUEST_SUCCESS: string = 'appointments/REQUEST_SUCCESS';
export const GET_PUBLIC_HOLIDAYS_SUCCESS: string = 'appointments/GET_PUBLIC_HOLIDAYS_SUCCESS';
export const GET_APPOINTMENTS_SUCCESS: string = 'appointments/GET_APPOINTMENTS_SUCCESS';
export const GET_APPOINTMENT_STATISTICS_SUCCESS: string = 'appointments/GET_APPOINTMENT_STATISTICS_SUCCESS';
export const SET_ITEMS_PAGE: string = 'appointments/SET_ITEMS_PAGE';
export const SET_FILTERS: string = 'appointments/SET_FILTERS';
export const CLEAR_FORM_DATA: string = 'appointments/CLEAR_FORM_DATA';
export const CLEAR_FORM_ERRORS: string = 'appointments/CLEAR_FORM_ERRORS';

/** Initial State */
const getinitialState = (): IAppointmentState => ({
	items: null,
	itemsPage: {
		currentPage: 0,
		totalPages: 0,
		totalItems: 0,
		items: [],
	},
	publicHolidays: null,
	memberId: null,
	officeId: null,
	mode: 'week',
	date: moment().toISOString(),
	rawDate: moment().toISOString(),
	appointmentStatistics: {
		thisWeek: {
			occupancy: 0,
			shopStatistics: [],
			total: 0,
		},
		thisMonth: {
			occupancy: 0,
			shopStatistics: [],
			total: 0,
		},
		thisYear: {
			occupancy: 0,
			shopStatistics: [],
			total: 0,
		},
		allTime: {
			occupancy: 0,
			shopStatistics: [],
			total: 0,
		},
	},
});

/** Reducer */
export function appointmentsReducer(state = getinitialState(), action: IAppointmentAction): IAppointmentState {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				error: false,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				message: action.payload.message,
				error: true,
			};

		case SET_ITEMS_PAGE:
			return {
				...state,
				isFetching: false,
				itemsPage: action.payload.itemsPage,
			};

		case GET_PUBLIC_HOLIDAYS_SUCCESS:
			return {
				...state,
				isFetching: false,
				publicHolidays: action.payload.publicHolidays,
			};

		case GET_APPOINTMENTS_SUCCESS:
			return {
				...state,
				isFetching: false,
				items: action.payload.items,
			};

		case GET_APPOINTMENT_STATISTICS_SUCCESS:
			return {
				...state,
				isFetching: false,
				appointmentStatistics: {
					...state.appointmentStatistics,
					...action.payload.appointmentStatistics,
				},
			};

		case CLEAR_FORM_DATA:
			return getinitialState();

		case CLEAR_FORM_ERRORS:
			return {
				...state,
				error: false,
				message: undefined,
			};

		case SET_FILTERS:
			if (!action.payload) {
				return state;
			}
			const keys: Array<keyof IAppointmentAction['payload']> = ['date', 'rawDate', 'memberId', 'mode', 'officeId'];
			const filters: IAppointmentAction['payload'] = {};
			for (const key of keys) {
				if (key in action.payload && action.payload[key] !== undefined) {
					filters[key] = action.payload[key];
				}
			}
			return {
				...state,
				...filters,
			};

		default:
			return state;
	}
}

export function setAppointmentFiltersMode(mode: IAppointmentState['mode']) {
	return (dispatch: Dispatch<IStore> /*, getState: () => IStore*/) => {
		const filters: Partial<IAppointmentFilters> = { mode };
		if (mode === 'day') {
			filters.memberId = '';
		}
		dispatch(setAppointmentFiltersAndFetch(filters));
	};
}

export function setAppointmentFiltersMember(
	memberId: IAppointmentState['memberId'],
	officeId?: IAppointmentState['officeId'],
) {
	if (!memberId) {
		return setAppointmentFiltersMode('day');
	}
	return setAppointmentFiltersAndFetch({ mode: 'week', memberId, officeId });
}

export function setAppointmentFiltersDate(date: string | Date) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const mode = getState().appointments.mode || 'week';
		const filters: Partial<IAppointmentFilters> = { mode, date: new Date(date).toISOString() };
		if (mode === 'day') {
			filters.memberId = null;
		}
		dispatch(setAppointmentFiltersAndFetch(filters));
	};
}

export function setAppointmentFiltersDateAndMember(
	date: string | Date,
	memberId: IAppointmentState['memberId'],
	officeId?: IAppointmentState['officeId'],
) {
	return (dispatch: Dispatch<IStore> /*, getState: () => IStore*/) => {
		const mode = memberId ? 'week' : 'day';
		const filters: Partial<IAppointmentFilters> = { mode, date: new Date(date).toISOString(), memberId, officeId };

		dispatch(setAppointmentFiltersAndFetch(filters));
	};
}

export function setAppointmentFiltersOffice(
	officeId: IOffice['id'],
	mode?: IAppointmentFilters['mode'],
	memberId?: IAppointmentFilters['memberId'],
) {
	return setAppointmentFiltersAndFetch({ officeId, mode: mode, memberId });
}

function setAppointmentFiltersAndFetch(filters: Partial<IAppointmentFilters>) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const officeId = filters.officeId || getState().appointments.officeId;
		const timezone = getState().manageShop?.shop?.offices?.find((o) => o.id === officeId)?.zoneInfo;
		const mode = filters.mode || getState().appointments.mode || 'day';
		const rawDate = filters.date || filters.rawDate || getState().appointments.rawDate || moment().toISOString();
		let date = normalizeDate(rawDate, mode, timezone);

		if (mode === 'day') {
			date = rawDate;
		}

		dispatch({
			type: SET_FILTERS,
			payload: {
				...filters,
				date,
				rawDate,
				mode,
				memberIds: mode === 'day' || !filters.memberId ? null : [filters.memberId],
			},
		});
		dispatch(getAppointments());
	};
}

/** Async Action Creator */
export function getAppointmentsByUser(currentPage?: number, shouldAppendData?: boolean) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const userId = getState().userPreferences.id;
		const page = currentPage || 1;

		const request: IOrdersRequest = {
			currentPage: page,
			itemsPerPage: c.APPOINTMENT_TAB_ITEMSPERPAGE,
			sortColumn: 'ProductSchedule DESC',
		};
		dispatch(requestStart());
		return requestHelper<{ items: IAppointmentWithCustomer[] }>(
			fetch(getState().fetchDefault.baseAPI + 'api/order/GetAppointmentsByUser/' + userId, {
				...getState().fetchDefault.requestInit,
				method: 'POST',
				body: JSON.stringify(request),
			}),
		)
			.then((res) => {
				const oldState = getState().appointments;
				const oldItems = oldState.items || [];
				const newItems = res.items as IAppointmentWithCustomer[];

				if (page === 1 || !shouldAppendData) {
					dispatch(getAppointmentsSuccess(newItems));
				} else {
					dispatch(getAppointmentsSuccess([...newItems, ...oldItems]));
				}

				dispatch(setItemsPage(res));
			})
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
			});
	};
}

export function getPublicHolidaysByOfficeId(officeId: string, year = new Date(Date.now()).getFullYear()) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		dispatch(requestStart());
		return requestHelper<string[]>(
			fetch(getState().fetchDefault.baseAPI + `api/Shop/Office/PublicHoliday/${officeId}/${year}`, {
				...getState().fetchDefault.requestInit,
				method: 'GET',
			}),
		)
			.then((res) => {
				const publicHolidays: IHoliday[] = res.map((str) => ({ type: 'holiday', date: str }));
				dispatch(getPublicHolidaysSuccess(publicHolidays));
			})
			.catch((err) => {
				console.error(err);
				// dispatch(requestFailure(err)); // error hide on calendar page if country is not supported for holidays api
			});
	};
}

export function addManualAppointment(
	form: Omit<IManualAppointment, 'resourceId'>,
	customer: ICustomer | undefined,
	itemRequest: IAddBookingItemRequest,
	currencyKey?: number,
) {
	return (dispatch: Dispatch<IStore>): Promise<boolean> => {
		const orderRequest: IOrderRequest = {
			email: customer?.email || form.email || undefined,
			userId: customer?.userId || undefined,
			shippingName: form.shippingName,
			shippingAddressLine: undefined,
			shippingZIPCode: undefined,
			shippingCity: undefined,
			shippingState: undefined,
			shippingCountryKey: undefined, //Slovenia
			shippingPhoneNumber: form.shippingPhoneNumber || customer.phoneNumber,
			shippingCallingCodeKey: (form.shippingPhoneNumber && form.shippingCallingCodeKey) || customer.callingCodeKey,
			shippingNote: undefined,

			billingName: form.shippingName,
			billingAddressLine: undefined,
			billingZIPCode: undefined,
			billingCity: undefined,
			billingState: undefined,
			billingCountryKey: undefined, //Slovenia

			currencyKey: currencyKey,
			languageKey: form?.languageKey || customer?.languageKey || undefined, // Updates existing customer's language if changed, if the customer is connected to a user then BE updates the customer's language according to their account
		};

		// Always clear basket before making an order for now
		return dispatch(clearShoppingBasket(false))
			.then(() => dispatch(addBookingToCart(itemRequest)))
			.then(() =>
				dispatch(createOrder(orderRequest, 'manual'))
					.then(() => dispatch(getAppointments(undefined, true, undefined)))
					.then(() => true)
					.catch((err) => {
						console.error(err);
						dispatch(requestFailure(err));
						return false;
					}),
			)
			.catch((err) => {
				dispatch(requestFailure(err));
				dispatch(clearShoppingBasket(false));
				return false;
			});
	};
}

export function addManualBookingItemToShoppingCart(itemRequest: IAddBookingItemRequest) {
	return (dispatch: Dispatch<IStore>): Promise<any> => {
		// Always clear basket before making an order for now
		return dispatch(clearShoppingBasket(false))
			.then(() => dispatch(addBookingToCart({ ...itemRequest, includeUnavailableResources: true })))
			.then((res) => {
				return Promise.resolve({ status: 'ok', result: res, message: 'Booking item added successfully' });
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				dispatch(clearShoppingBasket(false));
				return Promise.reject({ status: 'error', message: 'Failed adding booking item' });
			});
	};
}

export function createManualOrder(
	customer: ICustomer | undefined,
	currencyKey?: number,
	orderRequestData?: Partial<IManualOrderRequest>,
) {
	return (dispatch: Dispatch<IStore>): Promise<any> => {
		const orderRequest: IManualOrderRequest = {
			email: customer?.email || undefined,
			userId: customer?.userId || undefined,
			shippingName: customer?.name,
			shippingAddressLine: undefined,
			shippingZIPCode: undefined,
			shippingCity: undefined,
			shippingState: undefined,
			shippingCountryKey: undefined, //Slovenia
			shippingPhoneNumber: customer?.phoneNumber || undefined,
			shippingCallingCodeKey: customer?.callingCodeKey || undefined,
			shippingNote: undefined,

			billingName: customer?.name,
			billingAddressLine: undefined,
			billingZIPCode: undefined,
			billingCity: undefined,
			billingState: undefined,
			billingCountryKey: undefined, //Slovenia

			currencyKey: currencyKey,
			languageKey: customer?.languageKey || undefined,
			includeUnavailableResources: true, // overlapping appointments
			message: orderRequestData?.message || undefined,
			isSendOrderConfirmedSMSNotificationDisabled:
				orderRequestData?.isSendOrderConfirmedSMSNotificationDisabled || false, // don't send order confirmed SMS to customers if true (currently only for recurring events on manual booking)
		};

		// Always clear basket before making an order for now
		return dispatch(createOrder(orderRequest, 'manual'))
			.then((res) => {
				dispatch(
					notifSend({
						message: i18n.t('Notification_Success_Order_CreatedSuccess'),
						kind: 'success',
						dismissAfter: 3000,
					}),
				);
				return Promise.resolve({
					status: 'ok',
					message: i18n.t('Notification_Success_Order_CreatedSuccess'),
					result: res?.payload?.createdOrder,
				});
			})
			.catch((err) => {
				dispatch(requestFailure(err));
				dispatch(clearShoppingBasket(false));
				return Promise.reject({ status: 'error', message: 'Failed adding booking item' });
			});
	};
}

export function updateAppointmentsOnShopOrder(
	shopOrder: IShopOrder,
	itemsToUpdate: Array<BookingItemToUpdateRequest>,
	itemsToRemove: Array<string>,
	itemsToAdd: IAddBookingItemRequest[],
	shouldGetShopOrder: boolean = true,
) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		const orderRequest: IUpdateShopOrderRequest = {
			shopOrderId: shopOrder?.id,
			itemsToRemove,
			bookingItemsToAdd: itemsToAdd,
			bookingItemsToUpdate: itemsToUpdate,
			note: shopOrder?.note,
			enableNotifications: shopOrder?.enableNotifications,
		};
		return requestHelper(
			fetch(getState().fetchDefault.baseAPI + 'api/order/UpdateShopOrder', {
				...getState().fetchDefault.requestInit,
				method: 'PUT',
				body: JSON.stringify(orderRequest),
			}),
		)
			.then((res) => {
				if (shouldGetShopOrder) {
					return dispatch(getShopOrder(shopOrder?.id, false));
				} else {
					return Promise.resolve({ status: 'ok', message: 'Shop order updated successfully', response: res });
				}
			})
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
				return false;
			});
	};
}

export function groupRecurringAppointments(orderItems: IOrderItem[]) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore): Promise<any> => {
		const orderItemIds = orderItems.map((oi) => oi.id);
		return requestHelper(
			fetch(getState().fetchDefault.baseAPI + 'api/Order/GroupRecurringAppointments', {
				...getState().fetchDefault.requestInit,
				method: 'PUT',
				body: JSON.stringify(orderItemIds),
			}),
		)
			.then(() => {
				dispatch(requestSuccess());
				return { message: 'Recurring event successfully created', status: 'ok' };
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function getAppointmentsByCustomer(customerId: string, currentPage?: number, shouldAppendData?: boolean) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const page = currentPage || 1;

		const request: IOrdersRequest = {
			currentPage: page,
			itemsPerPage: c.APPOINTMENT_TAB_ITEMSPERPAGE,
			sortColumn: 'ProductSchedule DESC',
		};
		dispatch(requestStart());
		return requestHelper<{ items: IAppointmentWithCustomer[] }>(
			fetch(getState().fetchDefault.baseAPI + 'api/order/GetAppointmentsByCustomer/' + customerId, {
				...getState().fetchDefault.requestInit,
				method: 'POST',
				body: JSON.stringify(request),
			}),
		)
			.then((res) => {
				const oldState = getState().appointments;
				const oldItems = oldState.items || [];
				const newItems = res.items as IAppointmentWithCustomer[];

				if (page === 1 || !shouldAppendData) {
					dispatch(getAppointmentsSuccess(newItems));
				} else {
					dispatch(getAppointmentsSuccess([...oldItems, ...newItems]));
				}

				dispatch(setItemsPage(res));
			})
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function getAppointments(shopIdProp?: string, clearPreviousValues = true, mode?: Periodicity) {
	return (dispatch: Dispatch<IStore>, getState: () => IStore) => {
		const filters = getState().appointments;
		const shopId = shopIdProp || getState().manageShop?.shop?.id;
		const officeId = filters.officeId;
		const timezone = getState().manageShop?.shop?.offices?.find((o) => o.id === officeId)?.zoneInfo;
		// if (!filters.officeId || !shopId) {
		if (!shopId) {
			console.warn('Appointment filters (shop or office) not set', { shopId, filters });
			return;
		}
		const genWhen = (mode: Periodicity) => {
			if (mode === Periodicity.Week) {
				return {
					start: 'week',
					end: 'week',
				};
			} else if (mode === Periodicity.Month) {
				return {
					start: 'month',
					end: 'month',
				};
			} else if (mode === Periodicity.Year) {
				return {
					start: 'year',
					end: 'year',
				};
			} else if (mode === Periodicity.AllTime) {
				return {
					start: 'day',
					end: 'day',
				};
			}
		};
		const getDate = (from: boolean, to: boolean) => {
			if ((mode || mode === 0) && mode !== Periodicity.AllTime) {
				return new Date();
			} else if (mode && mode === Periodicity.AllTime) {
				if (from) {
					return new Date('2020-01-01');
				} else if (to) {
					return new Date('2100-01-01');
				}
			}
		};
		const request: IOrdersRequest = {
			offices: filters.officeId ? [filters.officeId] : [],
			currentPage: 1,
			itemsPerPage: 50000,
			dateFrom: momentWithTimezone(
				timezone,
				mode || mode === 0 ? getDate(true, false) : filters.date ? filters.date : '2020-01-01',
			)
				.startOf(mode || mode === 0 ? (genWhen(mode).start as unitOfTime.StartOf) : 'day')
				.toISOString(),
			dateTo: momentWithTimezone(
				timezone,
				mode || mode === 0 ? getDate(false, true) : filters.date ? filters.date : new Date(),
			)
				.add(mode || mode === 0 ? 0 : filters.mode === 'day' ? 0 : 6, 'day')
				.endOf(mode ? (genWhen(mode).end as unitOfTime.StartOf) : 'day')
				.toISOString(),
			sortColumn: 'ProductSchedule DESC',
		};
		dispatch(requestStart());
		return requestHelper<{ items: IAppointmentWithCustomer[] }>(
			fetch(getState().fetchDefault.baseAPI + 'api/order/GetAppointmentsByShop/' + shopId, {
				...getState().fetchDefault.requestInit,
				method: 'POST',
				body: JSON.stringify(request),
			}),
		)
			.then((res) => {
				const oldState = getState().appointments;
				const oldItems = oldState.items || [];
				const newItems = res.items as IAppointmentWithCustomer[];
				const allItems = clearPreviousValues ? [...newItems] : [...oldItems, ...newItems]; // Contains cancelled appointments
				for (let i = 0; i < allItems.length; i++) {
					const appointment = allItems[i];
					if (appointment.customer) {
						continue;
					}
					allItems[i] = {
						...appointment,
						customer: {
							name:
								appointment?.order?.email === 'walk-in@glossiq.com'
									? i18n.t('Customer_Walk-in')
									: appointment.order.shippingName,
							phoneNumber: appointment.order.shippingPhoneNumber,
							callingCodeKey: appointment.order.shippingCallingCodeKey,
							email: appointment?.order?.email === 'walk-in@glossiq.com' ? undefined : appointment.order.email,
						},
					};
				}
				dispatch(getAppointmentsSuccess(allItems));
				dispatch(setItemsPage(res));
			})
			.catch((err) => {
				console.error(err);
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function rejectShopOrder(shopOrderId: string, note: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			`${getState().fetchDefault.baseAPI}api/Order/${shopOrderId}/SetShopOrderStatus/${OrderStatus.Rejected}`,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: `"${note}"`,
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_ShopOrder_Rejected'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
					dispatch(getAppointments());
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getAppointmentStatistics(shopId: string, period: Periodicity, officeId?: string, timezone?: string) {
	return (dispatch, getState: () => IStore) => {
		let dateFrom = '';
		let dateTo = '';
		// This week
		if (period === Periodicity.Week) {
			dateFrom = momentWithTimezone(timezone).startOf('week').toISOString();
			dateTo = momentWithTimezone(timezone).endOf('week').toISOString();
		}
		// This month
		if (period === Periodicity.Month) {
			dateFrom = momentWithTimezone(timezone).startOf('month').toISOString();
			dateTo = momentWithTimezone(timezone).endOf('month').toISOString();
		}
		// This year
		if (period === Periodicity.Year) {
			dateFrom = momentWithTimezone(timezone).startOf('year').toISOString();
			dateTo = momentWithTimezone(timezone).endOf('year').toISOString();
		}
		// All time
		if (period === Periodicity.AllTime) {
			dateFrom = momentWithTimezone(timezone, '2020-01-01').toISOString();
			dateTo = momentWithTimezone(timezone).toISOString();
		}

		let queryString = '?';
		if (dateFrom) {
			queryString = queryString + 'dateFrom=' + dateFrom;
		}
		if (dateTo) {
			queryString = queryString + '&dateTo=' + dateTo;
		}
		if (officeId && officeId !== 'all') {
			queryString = queryString + '&officeId=' + officeId;
		}
		dispatch(requestStart());
		// Query needs to be updated to check from BTT to RTT or else it returns errors
		return fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/' + shopId + '/Appointments/' + queryString,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		)
			.then((res) => {
				if (res.ok) {
					res.json().then((result: IAppointmentStatistics) => {
						dispatch(getAppointmentStatisticsSuccess(result, period));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IAppointmentAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IAppointmentAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function requestSuccess(): IAppointmentAction {
	return {
		type: REQUEST_SUCCESS,
	};
}

/** Action Creator */
export function setItemsPage(itemsPage: IPage): IAppointmentAction {
	return {
		type: SET_ITEMS_PAGE,
		payload: {
			itemsPage,
		},
	};
}

/** Action Creator */
export function getPublicHolidaysSuccess(publicHolidays: IHoliday[]): IAppointmentAction {
	return {
		type: GET_PUBLIC_HOLIDAYS_SUCCESS,
		payload: {
			publicHolidays,
		},
	};
}

/** Action Creator */
export function getAppointmentsSuccess(items: IAppointment[]): IAppointmentAction {
	return {
		type: GET_APPOINTMENTS_SUCCESS,
		payload: {
			items,
		},
	};
}

/** Action Creator */
export function getAppointmentStatisticsSuccess(
	result: IAppointmentStatistics,
	periodicity: Periodicity,
): IAppointmentAction {
	if (periodicity === Periodicity.Week) {
		return {
			type: GET_APPOINTMENT_STATISTICS_SUCCESS,
			payload: {
				appointmentStatistics: {
					thisWeek: result,
				},
			},
		};
	} else if (periodicity === Periodicity.Month) {
		return {
			type: GET_APPOINTMENT_STATISTICS_SUCCESS,
			payload: {
				appointmentStatistics: {
					thisMonth: result,
				},
			},
		};
	} else if (periodicity === Periodicity.Year) {
		return {
			type: GET_APPOINTMENT_STATISTICS_SUCCESS,
			payload: {
				appointmentStatistics: {
					thisYear: result,
				},
			},
		};
	} else if (periodicity === Periodicity.AllTime) {
		return {
			type: GET_APPOINTMENT_STATISTICS_SUCCESS,
			payload: {
				appointmentStatistics: {
					allTime: result,
				},
			},
		};
	}
}

/** Action Creator */
export function clearAppointmentsData(): IAppointmentAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

export function clearAppointmentsErrors(): IAppointmentAction {
	return {
		type: CLEAR_FORM_ERRORS,
	};
}
